// web/src/notes/NotesPage.tsx
//
// /notes route — grouped (default) + chronological toggle with filter chips.
// URL is the source of truth for filter state via useSearchParams:
//   ?group_by=attachment|chronological
//   ?target_kind=cluster|analysis|material|freeform
//   ?archived=true
//   ?material_id=<id>   (placeholder — material chips deferred; see TODO below)
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useCopilotReadable } from "@copilotkit/react-core";
import { useTranslation } from "react-i18next";
import { meApi } from "@/api/me";
import type { NoteTargetKind, NotesListGrouped, NotesListChronological } from "../api/notes";
import { useListNotes, useCreateNote } from "./hooks";
import { NoteRow } from "./NoteRow";
import { NoteComposer } from "./NoteComposer";
import { Button } from "@/components/ui/button";

// TODO: Wire material chips when useMaterials() hook is available.
// import { useMaterials } from "../trading/useMaterials";

type GroupBy = "chronological" | "attachment";
type KindFilter = NoteTargetKind | "freeform" | null;

// (i18nLabelKey, value) tuples. The actual displayed label is fetched via
// t(`notes:filters.kinds.${labelKey}`) at render time.
const KIND_CHIPS: Array<{ labelKey: string; value: KindFilter }> = [
  { labelKey: "all", value: null },
  { labelKey: "cluster", value: "cluster" },
  { labelKey: "analysis", value: "analysis" },
  { labelKey: "material", value: "material" },
  { labelKey: "freeform", value: "freeform" },
];

export function NotesPage() {
  const { t } = useTranslation("notes");
  const [searchParams, setSearchParams] = useSearchParams();

  const material_id = searchParams.get("material_id");
  const target_kind = (searchParams.get("target_kind") as KindFilter) ?? null;
  const archived = searchParams.get("archived") === "true";
  const group_by = (searchParams.get("group_by") as GroupBy) ?? "attachment";

  // Slice 2 / Task F5: publish the current /notes filter state so the
  // backend per-turn composer (compose_per_turn_context_node) can include
  // a "Notes view: group_by=...; target_kind=...; ..." line in the
  // current_view block. Registered while on /notes, dropped on unmount —
  // CopilotKit automatically.
  useCopilotReadable({
    description: "notes_view_context",
    value: { group_by, target_kind, material_id, archived },
  });

  const [showFreeformComposer, setShowFreeformComposer] = useState(false);

  // Current user — needed to show edit/delete on own notes
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: meApi.get });
  const currentUserId = me?.user_id ?? null;

  const createMut = useCreateNote();

  const { data, isLoading } = useListNotes({
    material_id: material_id ?? undefined,
    target_kind: target_kind ?? undefined,
    archived,
    group_by,
    limit: 50,
  });

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === "") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next);
  }

  const isGrouped = group_by === "attachment";
  const groupedData = isGrouped ? (data as NotesListGrouped | undefined) : undefined;
  const chronoData = !isGrouped ? (data as NotesListChronological | undefined) : undefined;

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-4 p-4">
      {/* ── Header ── */}
      <header className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">{t("page.title")}</h1>
        <Button
          size="sm"
          onClick={() => setShowFreeformComposer((v) => !v)}
        >
          {showFreeformComposer ? t("actions.cancel") : t("actions.newFreeform")}
        </Button>
      </header>

      {/* ── Free-form composer ── */}
      {showFreeformComposer && (
        <NoteComposer
          isSubmitting={createMut.isPending}
          onCancel={() => setShowFreeformComposer(false)}
          onSubmit={(body) =>
            createMut.mutate(
              { body },
              { onSuccess: () => setShowFreeformComposer(false) }
            )
          }
        />
      )}

      {/* ── Filter chips + view toggle ── */}
      <div className="flex flex-wrap items-center gap-2">
        {KIND_CHIPS.map(({ labelKey, value }) => {
          const active = target_kind === value;
          return (
            <button
              key={labelKey}
              role="button"
              aria-pressed={active}
              onClick={() => setParam("target_kind", value)}
              className={[
                "rounded-full border px-3 py-0.5 text-xs font-medium transition-colors",
                active
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background text-foreground hover:bg-muted",
              ].join(" ")}
            >
              {t(`filters.kinds.${labelKey}`)}
            </button>
          );
        })}

        {/* TODO: Material chips — wire useMaterials() when the hook is available.
            materials.data?.items.map((m) => (
              <button key={m.id} ... onClick={() => setParam("material_id", material_id === m.id ? null : m.id)}>
                {m.id}
              </button>
            ))
        */}

        {/* ── Grouped / Chronological toggle ── */}
        <div className="ms-auto flex overflow-hidden rounded-md border border-border text-xs">
          <button
            role="button"
            onClick={() => setParam("group_by", "chronological")}
            className={[
              "px-3 py-1 transition-colors",
              group_by === "chronological"
                ? "bg-primary text-primary-foreground"
                : "hover:bg-muted",
            ].join(" ")}
          >
            {t("filters.groupBy.chronological")}
          </button>
          <button
            role="button"
            onClick={() => setParam("group_by", "attachment")}
            className={[
              "px-3 py-1 transition-colors",
              group_by === "attachment"
                ? "bg-primary text-primary-foreground"
                : "hover:bg-muted",
            ].join(" ")}
          >
            {t("filters.groupBy.attachment")}
          </button>
        </div>
      </div>

      {/* ── Archived toggle ── */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <label className="flex cursor-pointer items-center gap-1.5">
          <input
            type="checkbox"
            checked={archived}
            onChange={(e) =>
              setParam("archived", e.target.checked ? "true" : null)
            }
            className="h-3.5 w-3.5"
          />
          {t("filters.archived")}
        </label>
      </div>

      {/* ── List ── */}
      {isLoading ? (
        <p className="text-sm text-muted-foreground">{t("page.loading")}</p>
      ) : !isGrouped ? (
        /* Chronological flat list */
        <div className="flex flex-col gap-2">
          {!chronoData?.items?.length ? (
            <p className="text-sm text-muted-foreground">{t("page.empty")}</p>
          ) : (
            chronoData.items.map((n) => (
              <NoteRow key={n.id} note={n} currentUserId={currentUserId} />
            ))
          )}
        </div>
      ) : (
        /* Grouped view */
        <div className="flex flex-col gap-3">
          {!groupedData?.groups?.length ? (
            <p className="text-sm text-muted-foreground">{t("page.empty")}</p>
          ) : (
            groupedData.groups.map((g, i) => (
              <section
                key={i}
                className="overflow-hidden rounded-md border border-border"
              >
                <header className="border-b border-border bg-muted/40 px-3 py-2 text-sm font-medium">
                  {g.target.kind === "freeform" && t("kinds.freeformFull")}
                  {g.target.kind === "cluster" && (
                    <span>
                      {t("kinds.cluster")} ·{" "}
                      {g.target.title ?? g.target.id}
                    </span>
                  )}
                  {g.target.kind === "analysis" && (
                    <span>
                      {t("kinds.analysis")} ·{" "}
                      {g.target.material_id ?? g.target.id}
                    </span>
                  )}
                  {g.target.kind === "material" && (
                    <span>
                      {t("kinds.material")} ·{" "}
                      {g.target.display_name ?? g.target.id}
                    </span>
                  )}
                </header>
                <div className="flex flex-col gap-2 p-2">
                  {g.notes.map((n) => (
                    <NoteRow key={n.id} note={n} currentUserId={currentUserId} />
                  ))}
                </div>
              </section>
            ))
          )}
        </div>
      )}
    </div>
  );
}
